import { createSlice } from "@reduxjs/toolkit"
import {
  addCountry,
  deleteCountry,
  getAllCountries,
  getCountryDetails,
  updateCountry,
  updateCountryStatus,
} from "store/actions/countriesAction"
import {
  addIssueType,
  deleteIssueType,
  getAllIssueType,
  getAllSupportTicket,
  getIssueTypeDetails,
  getIssueTypeDropdownLists,
  getTicketDetails,
  updateBulkTickets,
  updateIssueTypeDetails,
  updateIssueTypeStatus,
  updateTicketStatus,
} from "store/actions/customerSupportAction"

const initialState = {
  loading: true,
  isLoading: false,
  itemsPerPage: 50,
  totalPages: null,
  currentPage: 1,
  issueTypes: [],
  issueTypeDetails: {},
  issueTypeDropdownLists: [],
  ticketLists: [],
  ticketDetails: {},
}

const usersSlice = createSlice({
  name: "Customer Support",
  initialState,
  reducers: {
    setItemsPerPage: (state, action) => {
      state.itemsPerPage = action.payload
    },
  },
  extraReducers: builder => {
    // GET ALL ISSUE
    builder
      .addCase(getAllIssueType.pending, state => {
        state.loading = true
      })
      .addCase(getAllIssueType.fulfilled, (state, action) => {
        state.loading = false
        const { status, data } = action.payload
        if (status) {
          const { totalCount, totalPages, currentPage } = action.payload
          state.issueTypes = data
          state.totalCount = totalCount
          state.totalPages = totalPages
          state.currentPage = currentPage
        }
      })
      .addCase(getAllIssueType.rejected, (state, action) => {
        state.loading = false
      })

    // UPDATE ISSUE STATUS
    builder
      .addCase(updateIssueTypeStatus.pending, state => {
        state.isLoading = true
      })
      .addCase(updateIssueTypeStatus.fulfilled, (state, action) => {
        state.isLoading = false
        const { status } = action.payload
        if (status) {
          const { id, status } = action.meta.arg
          state.issueTypes = state.issueTypes.map(item =>
            item._id === id ? { ...item, status: Number(status) } : item
          )
        }
      })
      .addCase(updateIssueTypeStatus.rejected, (state, action) => {
        state.isLoading = false
      })

    // ADD ISSUE TYPE
    builder
      .addCase(addIssueType.pending, state => {
        state.isLoading = true
      })
      .addCase(addIssueType.fulfilled, (state, action) => {
        state.isLoading = false
      })
      .addCase(addIssueType.rejected, (state, action) => {
        state.isLoading = false
      })

    // GET ISSUE TYPE DETAILS
    builder
      .addCase(getIssueTypeDetails.pending, state => {
        state.loading = true
        state.issueTypeDetails = {}
      })
      .addCase(getIssueTypeDetails.fulfilled, (state, action) => {
        state.loading = false
        const { status, data } = action.payload
        if (status) {
          state.issueTypeDetails = data
        }
      })
      .addCase(getIssueTypeDetails.rejected, (state, action) => {
        state.loading = false
      })

    //  UPDATE ISSUE TYPE
    builder
      .addCase(updateIssueTypeDetails.pending, state => {
        state.isLoading = true
      })
      .addCase(updateIssueTypeDetails.fulfilled, (state, action) => {
        state.isLoading = false
      })
      .addCase(updateIssueTypeDetails.rejected, (state, action) => {
        state.isLoading = false
      })

    // ISSUE TYPE DROPDOWN LISTS
    builder
      .addCase(getIssueTypeDropdownLists.pending, state => {
        state.isLoading = true
      })
      .addCase(getIssueTypeDropdownLists.fulfilled, (state, action) => {
        state.isLoading = false
        const { status, data } = action.payload
        if (status) {
          state.issueTypeDropdownLists = data
        }
      })
      .addCase(getIssueTypeDropdownLists.rejected, (state, action) => {
        state.isLoading = false
      })

    // DELETE ISSUE TYPE
    builder
      .addCase(deleteIssueType.pending, state => {
        state.isLoading = true
      })
      .addCase(deleteIssueType.fulfilled, (state, action) => {
        const { status } = action.payload
        const { id } = action.meta.arg
        state.isLoading = false
        if (status) {
          state.issueTypes = state.issueTypes.filter(item => item._id !== id)
          // Decrement the totalCount if it exists
          if (state.totalCount !== null) {
            state.totalCount -= 1
          }
        }
      })
      .addCase(deleteIssueType.rejected, (state, action) => {
        state.isLoading = false
      })

    builder
      .addCase(getAllSupportTicket.pending, state => {
        state.loading = true
        state.totalCount = null
        state.totalPages = null
        state.currentPage = null
      })
      .addCase(getAllSupportTicket.fulfilled, (state, action) => {
        state.loading = false
        const { status, data } = action.payload
        if (status) {
          const { totalCount, totalPages, currentPage } = action.payload
          state.ticketLists = data
          state.totalCount = totalCount
          state.totalPages = totalPages
          state.currentPage = currentPage
        }
      })
      .addCase(getAllSupportTicket.rejected, (state, action) => {
        state.loading = false
      })

    // GET TICKET DETAILS
    builder
      .addCase(getTicketDetails.pending, state => {
        state.loading = true
        state.ticketDetails = {}
      })
      .addCase(getTicketDetails.fulfilled, (state, action) => {
        state.loading = false
        const { status, data } = action.payload
        if (status) {
          state.ticketDetails = data
        }
      })
      .addCase(getTicketDetails.rejected, (state, action) => {
        state.loading = false
      })

    // UPDATE TICKET STATUS
    builder
      .addCase(updateTicketStatus.pending, state => {
        state.isLoading = true
      })
      .addCase(updateTicketStatus.fulfilled, (state, action) => {
        state.isLoading = false
        const { status } = action.payload
        if (status) {
          const { id, status } = action.meta.arg
          state.ticketLists = state.ticketLists.map(item =>
            item._id === id ? { ...item, status: Number(status) } : item
          )
        }
      })
      .addCase(updateTicketStatus.rejected, (state, action) => {
        state.isLoading = false
      })

    // UPDATE BULK TICKET STATUS
    builder
      .addCase(updateBulkTickets.pending, state => {
        state.isLoading = true
      })
      .addCase(updateBulkTickets.fulfilled, (state, action) => {
        state.isLoading = false
        const { status } = action.payload
        if (status) {
        }
      })
      .addCase(updateBulkTickets.rejected, (state, action) => {
        state.isLoading = false
      })
  },
})

export const { setItemsPerPage } = usersSlice.actions

export default usersSlice.reducer
